import { z } from 'zod';

/**
 * Common API Response Schemas
 * 
 * Epic 2 - Story 2.3: Runtime Type Safety
 * 
 * These schemas define the common structure for all API responses
 * in the password reset application.
 */

/**
 * Base API response structure
 * All API responses should conform to this shape
 */
export const ApiResponseSchema = z.object({
  errorType: z.number(),
  errorMessage: z.string().optional(),
  data: z.unknown().optional(),
});

export type ApiResponse = z.infer<typeof ApiResponseSchema>;

/**
 * Success response (errorType === 0)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ApiSuccessResponseSchema = (dataSchema: z.ZodType<any>) =>
  z.object({
    errorType: z.literal(0),
    data: dataSchema,
    errorMessage: z.undefined().optional(),
  });

/**
 * Error response (errorType !== 0)
 */
export const ApiErrorResponseSchema = z.object({
  errorType: z.number().refine((val) => val !== 0, {
    message: 'errorType must be non-zero for error responses',
  }),
  errorMessage: z.string(),
  data: z.undefined().optional(),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;

/**
 * Generic API result type
 * Use this for functions that return either data or error
 */
export type ApiResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };

/**
 * Helper to create typed success response
 */
export function createSuccessResult<T>(data: T): ApiResult<T> {
  return { success: true, data };
}

/**
 * Helper to create typed error response
 */
export function createErrorResult<T>(error: string): ApiResult<T> {
  return { success: false, error };
}

